import { UpdateType } from "./changelog";
import { InfrastructureStatus } from "./infrastructure";
import { ProjectStatus, ProjectType, Technology } from "./project";
import { ReZoneType } from "./zone";
import type { PublishableTypes } from "./publish";
import type { Point } from "geojson";

export type EntityType =
  | "project"
  | "network"
  | "substation"
  | "organisation"
  | "gas"
  | "lga"
  | "zone"
  | "custom"
  | "assessment"
  | "changelog";

export interface EntityBase {
  id: string;
  entity_type: EntityType;
  sources: Record<string, string>;
  [key: string]: unknown;
}

/**
 * Clickable entities on the map which show details and actions
 * Not necessary 1:1 with what's in the database (e.g. network and gas are both infrastructure)
 */
export const entityTypeNames: Record<EntityType, string> = {
  project: "Project",
  zone: "Renewable Energy Zone",
  organisation: "Organisation",
  substation: "Substation",
  gas: "Gas Pipeline",
  network: "Network",
  custom: "Site",
  lga: "Local Government Area",
  assessment: "Assessment",
  changelog: "Update",
};

// entities that can be selected
export type EntitySelection =
  | Pick<ProjectRow, "id" | "entity_type" | "project_name_prj">
  | Pick<NetworkRow, "id" | "entity_type" | "infrastructure_name_net">
  | Pick<OrganisationRow, "id" | "entity_type" | "organisation_name_org">
  | Pick<SubstationRow, "id" | "entity_type" | "substation_name_sub">
  | GasRow
  | LgaRow
  | ZoneRow
  | CustomLayerRow
  | Pick<ChangelogRow, "id" | "entity_type">; // changelog can be hovered, not selected

export type EntitySelectionRow =
  ProjectRow | NetworkRow | OrganisationRow | SubstationRow | GasRow | ZoneRow;

export const getMapFeatureId = (entity: EntitySelection) => {
  if (entity.entity_type === "custom") {
    return entity.localId;
  }
  return entity.id;
};

export const selectionEqual = (
  a: EntitySelection | undefined,
  b: EntitySelection | undefined,
) => {
  if (!a || !b) {
    return a === b; // Both undefined = true, one undefined = false
  }

  if (a.entity_type !== b.entity_type) {
    return false;
  }

  // For custom layers, compare by both localId (the layer) and id (the feature within the layer)
  // This ensures different features within the same custom layer are treated as distinct
  if (a.entity_type === "custom" && b.entity_type === "custom") {
    return a.localId === b.localId && a.id === b.id;
  }

  return getMapFeatureId(a) === getMapFeatureId(b);
};

export interface EntityRegistryRow {
  entity_id: string;
  entity_type: EntityType;
  entity_name: string;
  entity_information: string;
  point: Point | null;
  technology: string | null;
}

export type EntityRegistry = Record<string, EntityRegistryRow>;

// The subset of PublishableTypes where each row represents an entity in entity registry
export const ENTITY_TABLES = [
  "projects",
  "infrastructure_v5",
  "organisations",
  "substations_v1",
] as const satisfies readonly PublishableTypes[];

export type EntityTable = (typeof ENTITY_TABLES)[number];

export interface EntityLink {
  source_id: string;
  target_id: string;
  entity_link_type: string;
  friendly_name: string;
  field_name: string;
}

export const getEntityName = (entity: EntityRow | null) => {
  switch (entity?.entity_type) {
    case "organisation":
      return entity.organisation_name_org;
    case "project":
      return entity.project_name_prj;
    case "network":
      return entity.infrastructure_name_net;
    case "substation":
      return entity.substation_name_sub;
  }
  return "Unknown";
};

// V2 row types keyed by field_key

export interface ProjectFields {
  project_name_prj: string;
  technology_prj: Technology;
  project_status_prj: ProjectStatus;
  project_type_prj: ProjectType;
  project_size_mw_prj: number | null;
  storage_size_mwh_prj: number | null;
  turbines_prj: number | null;
  network_prj: string | null;
  state_prj: string;
  country_prj: string | null;
  opennem_prj: string | null;
  exact_point_prj: boolean;
  point_prj: [number, number] | null;
  has_geom_prj: boolean;

  ppa_corp_qty_prj: string | null;
  nsw_ida_prj: string | null;
  nsp_prj: string[] | null;
  recent_update_prj: Date | null;
  ferm: string | null;
  watchlist_prj: boolean;
  app_status_prj: string | null;
  turbine_mw_prj: number[] | null;
  cis_prj: string | null;
  rez_state_prj: string[] | null;
  aemo_rez_2024_prj: string[] | null;
  dc_capacity_prj: number | null;
  eis_source_prj: string | null;
  da_source_prj: string | null;
  epbc_referral_decision_prj: Date | null;
  nsw_forestry_prj: string | null;
  cefc_fund_prj: number | null;
  arena_fund_prj: number | null;
  ltesa_prj: string | null;
  turbine_oem_prj: string[] | null;
  batt_tech_prj: string | null;
  owners_prj: string[] | null;
  councils_prj: string[] | null;
  ppa_util_offtaker_prj: string | null;
  arena_lrgb_prj: number | null;
  storage_hours_prj: number | null;
  on_screen_prj: boolean;
  offshore_licence_prj: string | null;
  arena_h2_prj: number | null;
  aemo_kci_prj: string | null;
  cer_code_prj: string | null;
  conn_point_prj: string | null;
  trans_node_prj: string | null;
  rez_fed_prj: string[] | null;
  data_provider_prj: string[] | null;
  abandoned_prj: Date | null;
  arena_arp_prj: number | null;
  model_prj: string | null;
  aemo_rez_2026_prj: string[] | null;
  id_prj: string | null;
  mlf_prj: number | null;
  emissions_prj: number | null;
  production_prj: number | null;
  vwap_prj: number | null;
  curtailment_prj: number | null;
  ppa_util_dur_prj: string | null;
  hub_height_prj: number[] | null;
  eis_status_prj: string | null;
  da_lapse_prj: Date | null;
  epbc_referral_status_prj: string | null;
  da_duration_prj: number | null;
  eis_referral_start_prj: Date | null;
  eis_decision_prj: Date | null;
  da_submission_prj: Date | null;
  epbc_code_prj: string | null;
  batt_config_prj: string | null;
  app_type_prj: string | null;
  rotor_tip_height_prj: number[] | null;
  gen_class_prj: string | null;
  commercial_ops_prj: Date | null;
  duid_prj: string[] | null;
  vret_prj: string | null;
  nrepl_prj: string | null;
  epbc_referral_received_prj: Date | null;
  da_approval_prj: Date | null;
  ppa_corp_dur_prj: string | null;
  aemo_commissioning_prj: Date | null;
  aemo_rez_2025_prj: string[] | null;
  ppa_util_qty_prj: string | null;
  batt_oem_prj: string[] | null;
  aemo_in_service_prj: Date | null;
  initial_ops_prj: Date | null;
  arena_rd_prj: number | null;
  aemo_announced_prj: Date | null;
  rotor_diameter_prj: number[] | null;
  aemo_sub_region_prj: string[] | null;
  epbc_assessment_approach_prj: string | null;
  project_group_prj: string[] | null;
  fin_inv_decision_prj: Date | null;
  conn_voltage_prj: number[] | null;
  abs_sa2_prj: string[] | null;
  nsw_ssp_prj: string | null;
  contractors_prj: string[] | null;
  arena_rar_prj: number | null;
  batt_control_prj: string[] | null;
  secondary_fuel_prj: string | null;
  aemo_anticipated_prj: Date | null;
  aemo_committed_prj: Date | null;
  cer_accred_prj: Date | null;
  nsw_rez_cwo_prj: string | null;
  ppa_corp_offtaker_prj: string | null;
  da_last_modified_prj: Date | null;
  epbc_assessment_status_prj: string | null;
  epbc_referral_start_prj: Date | null;
  proposed_closure_prj: Date | null;
  orgs_prj: string[] | null;
  fed_electorate_prj: string[] | null;
  pv_mount_prj: string | null;
  nsw_panel_status_prj: string | null;
  arena_asi_prj: number | null;
  aemo_gen_status_prj: string | null;
  epbc_status_prj: string | null;
  da_status_prj: string | null;
  epbc_outcome_prj: Date | null;
  arena_erp_prj: number | null;
  nsw_rez_sw_prj: string | null;
  gas_cycle_prj: string | null;
}

export interface NetworkFields {
  bbox_net: [number, number, number, number] | null;
  infrastructure_name_net: string;
  infrastructure_status_net: InfrastructureStatus;
  infrastructure_type_net: "network";
  voltage_net: number | null;
  network_net: string | null;
  construction_type_net: string | null;
  country_net: string;
  line_id_net: string | null;
  state_net: string | null;

  dyn_emergency_net: string | null;
  da_approval_net: Date | null;
  da_duration_net: number | null;
  da_lapse_net: Date | null;
  epbc_status_net: string | null;
  da_last_modified_net: Date | null;
  da_source_net: string | null;
  da_status_net: string | null;
  da_submission_net: Date | null;
  static_normal_net: string | null;
  dyn_normal_net: string | null;
  operator_net: string[] | null;
  epbc_assessment_approach_net: string | null;
  epbc_assessment_status_net: string | null;
  epbc_code_net: string | null;
  static_load_shedding_net: string | null;
  static_winter_normal_net: string | null;
  epbc_outcome_net: Date | null;
  epbc_referral_decision_net: Date | null;
  epbc_referral_received_net: Date | null;
  epbc_referral_start_net: Date | null;
  epbc_referral_status_net: string | null;
  dyn_winter_emergency_net: string | null;
  static_summer_normal_net: string | null;
  static_summer_emergency_net: string | null;
  dyn_winter_normal_net: string | null;
  dyn_winter_load_shedding_net: string | null;
  dyn_summer_emergency_net: string | null;
  dyn_summer_load_shedding_net: string | null;
  static_winter_load_shedding_net: string | null;
  eis_decision_net: Date | null;
  eis_referral_start_net: Date | null;
  eis_source_net: string | null;
  eis_status_net: string | null;
  static_winter_emergency_net: string | null;
  static_summer_load_shedding_net: string | null;
  static_emergency_net: string | null;
  nsw_ssp_net: string | null;
  nsw_panel_status_net: string | null;
  on_screen_net: boolean;
  source_text_net: string | null;
  dyn_load_shedding_net: string | null;
  dyn_summer_normal_net: string | null;
  id_net: string | null;
  headroom_net: number | null;
  headroompct_net: number | null;
  nrepl_net: string | null;
  construction_start_net: Date | null;
  substation_terminal_net: string[] | null;
}

export interface OrgansiationFields {
  organisation_name_org: string;
  url_org: string | null;
  active_org: boolean;
  tags_org: string[];
  logo_flag_org: boolean;

  on_screen_org: boolean;
  organisations_org: string[] | null;
  roles_org: string[] | null;
  project_count_org: number | null;
  total_mw_org: number | null;
  id_org: string | null;

  linked_projects_org: Record<string, string[]>;
  linked_projects_by_role_org: Record<string, string[]>;
  linked_networks_org: string[];
}

export interface SubstationFields {
  substation_name_sub: string;
  substation_status_sub: string;
  voltage_sub: number;
  point_sub: [number, number] | null;

  da_approval_sub: Date | null;
  da_duration_sub: number | null;
  da_lapse_sub: Date | null;
  da_last_modified_sub: Date | null;
  da_source_sub: string | null;
  da_status_sub: string | null;
  da_submission_sub: Date | null;
  epbc_assessment_approach_sub: string | null;
  epbc_assessment_status_sub: string | null;
  epbc_code_sub: string | null;
  epbc_outcome_sub: Date | null;
  epbc_referral_decision_sub: Date | null;
  epbc_referral_received_sub: Date | null;
  epbc_referral_start_sub: Date | null;
  epbc_referral_status_sub: string | null;
  epbc_status_sub: string | null;
  eis_decision_sub: Date | null;
  eis_referral_start_sub: Date | null;
  eis_source_sub: string | null;
  eis_status_sub: string | null;
  nsw_ssp_sub: string | null;
}

export interface ChangelogFields {
  title_cl: string;
  timestamp_cl: Date;
  update_type_cl: UpdateType;
  group_name_cl: string | null;
  group_rank_cl: number | null;
  related_entity_id_cl: string | null;
  linked_entity_id_cl: string | null;
  field_key_cl: string | null;
  zoom_cl: number | null;
  link_text_cl: string | null;
  link_url_cl: string | null;
  description_cl: string;
  point_cl: [number, number] | null;
  read_cl: boolean;
}

// entities from the bootstrap
export type EntityRow =
  ProjectRow | NetworkRow | OrganisationRow | SubstationRow | ChangelogRow;

export interface ProjectRow extends EntityBase, ProjectFields {
  entity_type: "project";
}

export interface NetworkRow extends EntityBase, NetworkFields {
  entity_type: "network";
}

export interface OrganisationRow extends EntityBase, OrgansiationFields {
  entity_type: "organisation";
}

export interface SubstationRow extends EntityBase, SubstationFields {
  entity_type: "substation";
}

export interface GasRow extends EntityBase {
  entity_type: "gas";
  infrastructure_name_gas: string;
  infrastructure_status_gas: string;
  source_text_gas: string | null;
}

export interface ZoneRow extends EntityBase {
  entity_type: "zone";
  zone_name_zne: string;
  type_zne: ReZoneType;
  state_zne: string;
}

export interface LgaRow extends EntityBase {
  entity_type: "lga";
  zone_name_lga: string;
  state_lga: string;
}

export interface CustomLayerRow extends EntityBase {
  entity_type: "custom";
  localId: string;
}

export interface BaseChangelogRow extends EntityBase, ChangelogFields {
  entity_type: "changelog";
  related_entity_type_cl: null;
}

export interface ProjectChangelogRow
  extends EntityBase, ChangelogFields, ProjectFields {
  entity_type: "changelog";
  related_entity_type_cl: "project";
  related_entity_id_cl: string;
}

export interface NetworkChangelogRow
  extends EntityBase, ChangelogFields, NetworkFields {
  entity_type: "changelog";
  related_entity_type_cl: "network";
  related_entity_id_cl: string;
}

export interface OrganisationChangelogRow
  extends EntityBase, ChangelogFields, OrgansiationFields {
  entity_type: "changelog";
  related_entity_type_cl: "organisation";
  related_entity_id_cl: string;
}

export type ChangelogRow =
  | BaseChangelogRow
  | ProjectChangelogRow
  | NetworkChangelogRow
  | OrganisationChangelogRow;

// entities from map feature properties
export type EntityFeature =
  | ProjectFeature
  | NetworkFeature
  | GasFeature
  | SubstationFeature
  | ZoneFeature
  | LgaFeature
  | ChangelogFeature
  | CustomLayerRow;

// projects-geom, projects-points
export type ProjectFeature = Pick<
  ProjectRow,
  | "id"
  | "entity_type"
  | "project_name_prj"
  | "technology_prj"
  | "project_size_mw_prj"
  | "storage_size_mwh_prj"
  | "project_status_prj"
  | "exact_point_prj"
  | "point_prj"
>;

// network-line, network-geom
export type NetworkFeature = Pick<
  NetworkRow,
  | "id"
  | "entity_type"
  | "sources"
  | "source_text_net"
  | "infrastructure_name_net"
  | "infrastructure_status_net"
  | "voltage_net"
  | "construction_type_net"
  | "line_id_net"
  | "state_net"
  | "country_net"
  | "network_net"
>;

// gas-line
export type GasFeature = Pick<
  GasRow,
  | "id"
  | "entity_type"
  | "sources"
  | "source_text_gas"
  | "infrastructure_name_gas"
  | "infrastructure_status_gas"
>;

// substations-geom
export type SubstationFeature = Pick<
  SubstationRow,
  | "id"
  | "entity_type"
  | "substation_name_sub"
  | "substation_status_sub"
  | "voltage_sub"
>;

// rez-geom, rez-point
export type ZoneFeature = Pick<
  ZoneRow,
  "id" | "entity_type" | "sources" | "zone_name_zne" | "state_zne" | "type_zne"
>;

// lga-geom, lga-point
export type LgaFeature = Pick<
  LgaRow,
  "id" | "entity_type" | "sources" | "zone_name_lga" | "state_lga"
>;

// changelog-points
export type ChangelogFeature = Pick<
  BaseChangelogRow,
  | "id"
  | "entity_type"
  | "timestamp_cl"
  | "title_cl"
  | "description_cl"
  | "update_type_cl"
>;
